import { create } from 'zustand';
import { marketplaceService } from '../services/marketplace';
import { useAuthStore } from './authStore';
import type { OperationMode } from '../types/marketplace';
import { toErrorMessage, type MarketplaceState } from './marketplaceStoreTypes';
import { createOperatorSlice } from './operatorSlice';
import { createRequestSlice } from './requestSlice';
import { createContractSlice } from './contractSlice';
import { createReturnIntentSlice } from './returnIntentSlice';

export type { MarketplaceState } from './marketplaceStoreTypes';

export const MARKETPLACE_MODE_ONBOARDED_KEY = 'marketplace_mode_onboarded';

function updateAuthUserMode(mode: OperationMode): void {
    useAuthStore.setState((state) => ({
        user: state.user ? { ...state.user, operation_mode: mode } : state.user,
    }));
}

export const useMarketplaceStore = create<MarketplaceState>((set, get) => ({
    operationMode: null,
    modeLoaded: false,
    isLoadingMode: false,

    ...createOperatorSlice(set, get),
    ...createRequestSlice(set, get),
    ...createContractSlice(set, get),
    ...createReturnIntentSlice(set),

    error: null,

    fetchMode: async () => {
        set({ isLoadingMode: true, error: null });

        try {
            const response = await marketplaceService.getProfileMode();
            const operationMode = response.data.operation_mode;

            set({
                operationMode,
                modeLoaded: true,
                isLoadingMode: false,
            });

            updateAuthUserMode(operationMode);
        } catch (error) {
            set({
                isLoadingMode: false,
                modeLoaded: true,
                error: toErrorMessage(error, 'No se pudo cargar el modo actual.'),
            });
        }
    },

    updateMode: async (mode) => {
        set({ isLoadingMode: true, error: null });

        try {
            const response = await marketplaceService.updateProfileMode(mode);
            const updatedMode = response.data.operation_mode;

            localStorage.setItem(MARKETPLACE_MODE_ONBOARDED_KEY, '1');

            set({
                operationMode: updatedMode,
                modeLoaded: true,
                isLoadingMode: false,
            });

            updateAuthUserMode(updatedMode);

            return true;
        } catch (error) {
            set({
                isLoadingMode: false,
                error: toErrorMessage(error, 'No se pudo actualizar el modo de operación.'),
            });
            return false;
        }
    },

    clearError: () => {
        set({ error: null });
    },

    markModeOnboarded: () => {
        localStorage.setItem(MARKETPLACE_MODE_ONBOARDED_KEY, '1');
    },
}));
